import {
  calculateMonthlyPrice,
  removeTrailingZeros,
} from '@forest/paywall-core'
import { cn } from '@forest/shared'
import { useStore } from '@nanostores/react'
import { AnimatePresence } from 'motion/react'
import { Dialog } from 'radix-ui'
import { useState } from 'react'
import { useChinaAndroidTrialMutation } from '~/hooks/use-china-android-trial-mutation'
import { useSubscribeMutation } from '~/hooks/use-subscribe-mutation'
import { useTrialEligibility } from '~/hooks/use-trial-eligibility'
import { motion } from '~/lib/motion'
import { m } from '~/paraglide/messages'
import { language, platform } from '~/params'
import { native } from '@forest/native-services'
import type { Product } from '@forest/native-services'
import {
  $annualEarlyBirdProduct,
  $chinaAndroidTrialEligibility,
  $products,
} from '~/store'
import { $subscribedView } from '~/store/subscribed-view'
import { SubscribeFlowBg } from './graphics/subscribe-flow-bg'
import { BackButtonIcon } from './icons/back-button'
import { BellIcon } from './icons/bell'
import { CheckIcon } from './icons/check'
import { DecoBar } from './icons/deco-bar'
import { NotIncludedIcon } from './icons/not-included'
import { StarIcon } from './icons/star'
import { UnlockIcon } from './icons/unlock'
import { PlanOption } from './plan-option'

type Step = 'comparison' | 'reminder' | 'plan-select'

export function SubscribeDialog(props: {
  isOpen: boolean
  isPremium: boolean
  onClose: () => void
}) {
  const [step, setStep] = useState<Step>('comparison')
  const product = useStore($annualEarlyBirdProduct)
  const { isChinaAndroidTrial, isTrial } = useTrialEligibility()

  const chinaAndroidTrialMutation = useChinaAndroidTrialMutation({
    onSuccess: () => {
      $chinaAndroidTrialEligibility.set('unavailable')
      native.user.refreshServiceLevel()
      $subscribedView.set({ isOpen: true, isTrial: true })
      props.onClose()
    },
  })

  const subscribeMutation = useSubscribeMutation({
    onSuccess: () => {
      $subscribedView.set({ isOpen: true, isTrial })
      props.onClose()
    },
  })

  const activeMutation = isChinaAndroidTrial
    ? chinaAndroidTrialMutation
    : subscribeMutation

  const handleSubscribe = (selectedProduct: Product) => {
    if (isChinaAndroidTrial) {
      chinaAndroidTrialMutation.mutate()
      return
    }
    subscribeMutation.mutate({
      product: selectedProduct,
      isTrial: isTrial && selectedProduct.eligible_trial_days > 0,
    })
  }

  return (
    <Dialog.Root
      open={props.isOpen}
      onOpenChange={(open) => {
        if (!open && !activeMutation.isPending) {
          props.onClose()
          setStep('comparison')
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Content
          className="fixed inset-0 z-30 flex flex-col overflow-y-hidden bg-[#001721] focus-visible:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          aria-describedby=""
        >
          <div className="pointer-events-none absolute left-0 right-0 top-0 -z-10 h-[900px] overflow-x-hidden bg-top">
            <div className="absolute left-1/2 top-0 h-full -translate-x-1/2">
              <SubscribeFlowBg />
            </div>
          </div>

          <Dialog.Title className="sr-only">{m.btn_unlock_plus()}</Dialog.Title>
          <AnimatePresence mode="popLayout" initial={false}>
            {step === 'comparison' && (
              <motion.div
                key="comparison"
                className="flex h-full flex-col"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <ComparisonView
                  isPremium={props.isPremium}
                  isTrial={isTrial}
                  isChinaAndroidTrial={isChinaAndroidTrial}
                  isPending={activeMutation.isPending}
                  onBack={props.onClose}
                  onNext={() =>
                    isTrial && !isChinaAndroidTrial
                      ? setStep('reminder')
                      : setStep('plan-select')
                  }
                  onSubscribe={() => {
                    if (!product) return
                    handleSubscribe(product)
                  }}
                />
              </motion.div>
            )}

            {step === 'reminder' && (
              <motion.div
                key="reminder"
                className="flex h-full flex-col"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <ReminderView
                  isPending={activeMutation.isPending}
                  onBack={() => setStep('comparison')}
                  onNext={() => setStep('plan-select')}
                />
              </motion.div>
            )}

            {step === 'plan-select' && (
              <motion.div
                key="plan-select"
                className="flex h-full flex-col"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <PlanSelectView
                  isTrial={isTrial}
                  isPending={activeMutation.isPending}
                  onBack={() =>
                    isTrial && !isChinaAndroidTrial
                      ? setStep('reminder')
                      : setStep('comparison')
                  }
                  onSubscribe={handleSubscribe}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// ---------------------------------------------------------------------------
// Comparison view
// ---------------------------------------------------------------------------

function ComparisonView(props: {
  isPremium: boolean
  isTrial: boolean
  isChinaAndroidTrial: boolean
  isPending: boolean
  onBack: () => void
  onNext: () => void
  onSubscribe: () => void
}) {
  const product = useStore($annualEarlyBirdProduct)
  const isChinaAndroid = platform === 'china_android'

  return (
    <>
      {/* Nav bar */}
      <div className="absolute left-0 right-0 top-0 mx-auto flex w-full max-w-[640px] justify-between px-4 pt-[calc(16px+var(--safe-area-top))]">
        <button
          className="size-[42px] rounded-[50%] bg-white/80 backdrop-blur"
          onClick={props.onBack}
        >
          <BackButtonIcon />
        </button>
        <div></div>
      </div>

      {/* Scrollable content */}
      <div className="h-full space-y-12 overflow-y-auto px-8 pb-48 pt-[120px]">
        <h2 className="whitespace-pre-line px-4 text-center text-2xl font-bold text-white">
          {props.isPremium
            ? m.subscription_walkthrough_p2_pro_title_2()
            : m.subscription_walkthrough_p2_free_title()}
        </h2>

        <ComparisonTable
          isPremium={props.isPremium}
          className="mx-auto max-w-[640px]"
        />
      </div>

      {/* Fixed bottom CTA */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-[#001721] px-6 pb-12 pt-3"
        style={{
          boxShadow: '0 -3px 3px 0 rgba(255, 255, 255, 0.20)',
        }}
      >
        <div className="mx-auto max-w-[640px] space-y-3">
          <button
            className="group h-[52px] w-full rounded-xl p-px [background-image:linear-gradient(180deg,#C5FF70_0%,#69FF78_45%,#30D3FF_100%)]"
            disabled={props.isPending}
            onClick={
              props.isChinaAndroidTrial ? props.onSubscribe : props.onNext
            }
          >
            <span className="flex h-full w-full items-center justify-center rounded-[11px] font-bold text-[#176361] [background-image:linear-gradient(106deg,#C5FF70_13%,#30D3FF_97%)] group-active:[background-image:linear-gradient(90deg,#94D66E_0%,#2BB8D3_100%)]">
              {props.isChinaAndroidTrial
                ? m.btn_try_plus_free()
                : props.isTrial
                  ? m.btn_how_trial_works()
                  : m.choose_plan()}
            </span>
          </button>

          {!isChinaAndroid && !props.isTrial && (
            <p className="w-full text-center text-sm text-white/90">
              {m.new_user_yearly_subscription_footer({
                yearly_price_month: product
                  ? calculateMonthlyPrice(product)
                  : '',
              })}
            </p>
          )}
        </div>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Comparison table
// ---------------------------------------------------------------------------

interface ComparisonFeature {
  name: string
  currentUser: boolean
  plusVersion: boolean
}

const featuresForFreeUser: ComparisonFeature[] = [
  { name: m.focus_timer(), currentUser: true, plusVersion: true },
  { name: m.triple_coin_reward(), currentUser: false, plusVersion: true },
  { name: m.custom_app_allow_list(), currentUser: false, plusVersion: true },
  { name: m.free_use_crystal_tree(), currentUser: false, plusVersion: true },
  { name: m.focus_trend_analysis(), currentUser: false, plusVersion: true },
  { name: m.plant_tree_for_earth(), currentUser: false, plusVersion: true },
  { name: m.no_ads(), currentUser: false, plusVersion: true },
]

const featuresForProUser: ComparisonFeature[] = [
  { name: m.all_original_pro_features(), currentUser: true, plusVersion: true },
  { name: m.triple_coin_reward(), currentUser: false, plusVersion: true },
  { name: m.plus_exclusive_tree(), currentUser: false, plusVersion: true },
  { name: m.seasonal_crystal_trees(), currentUser: false, plusVersion: true },
  { name: m.app_block(), currentUser: false, plusVersion: true },
]

function ComparisonTable(props: { isPremium: boolean; className?: string }) {
  const features = props.isPremium ? featuresForProUser : featuresForFreeUser
  return (
    <div className={cn('w-full', props.className)}>
      <div className="relative flex w-full flex-col">
        <div className="pointer-events-none absolute bottom-0 right-[-6px] top-0 w-[69px] rounded-[10px] bg-white/20"></div>

        <div className="flex h-[34px] items-center justify-end">
          <div className="flex items-center gap-4">
            <span className="w-11 text-center text-sm font-semibold text-white">
              {props.isPremium ? m.pro() : m.free()}
            </span>
            <span className="flex h-[22px] w-[57px] items-center justify-center rounded border-[2px] border-white bg-gradient-to-r from-[#81D200] via-[#0EBC73] to-[#08C1F3] text-center text-xs font-bold text-white">
              {'PLUS'}
            </span>
          </div>
        </div>

        {features.map((feature, index) => (
          <div
            key={index}
            className="flex h-[46px] items-center justify-between border-white/30 [&:not(:last-child)]:border-b"
          >
            <span className="text-sm text-white">{feature.name}</span>
            <div className="flex items-center gap-11 pr-4">
              {feature.currentUser ? <CheckIcon /> : <NotIncludedIcon />}
              {feature.plusVersion ? <CheckIcon /> : <NotIncludedIcon />}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Reminder view
// ---------------------------------------------------------------------------

function ReminderView(props: {
  isPending: boolean
  onBack: () => void
  onNext: () => void
}) {
  const product = useStore($annualEarlyBirdProduct)
  const freeTrialDays = product?.eligible_trial_days ?? 0

  return (
    <>
      {/* Nav bar */}
      <div className="absolute left-0 right-0 top-0 mx-auto flex w-full max-w-[640px] justify-between px-4 pt-[calc(16px+var(--safe-area-top))]">
        <button
          className="size-[42px] rounded-[50%] bg-white/80 backdrop-blur"
          onClick={props.onBack}
          disabled={props.isPending}
        >
          <BackButtonIcon />
        </button>
        <div></div>
      </div>

      {/* Scrollable content */}
      <div className="h-full space-y-12 overflow-y-auto px-8 pb-48 pt-[120px]">
        <h2
          className="whitespace-pre-line px-4 text-center text-2xl font-bold text-white"
          dangerouslySetInnerHTML={{
            __html: m
              .subscription_walkthrough_p3_title()
              .replace('[%2$s]', 'color: #2AFD00;'),
          }}
        />

        <div className="relative mx-auto mt-5 flex max-w-[640px] flex-col gap-8 px-6 text-white">
          <DecoBar className="pointer-events-none absolute left-[41px] top-3" />

          <div className="relative flex gap-[10px] pl-[6px]">
            <div className="size-[30px] shrink-0">
              <UnlockIcon />
            </div>
            <div className="flex-1 space-y-1">
              <h3 className="font-bold">
                {m.subscription_walkthrough_p3_today()}
              </h3>
              <p className="text-sm">
                {m.subscription_walkthrough_p3_today_note()}
              </p>
            </div>
          </div>

          <div className="relative flex gap-[10px] pl-[6px]">
            <div className="size-[30px] shrink-0">
              <BellIcon />
            </div>
            <div className="flex-1 space-y-1">
              <h3 className="font-bold">
                {m.subscription_walkthrough_p3_1_day_before_trail()}
              </h3>
              <p className="text-sm">
                {m.subscription_walkthrough_p3_1_day_before_trail_note()}
              </p>
            </div>
          </div>

          <div className="relative flex gap-[10px] pl-[6px]">
            <div className="size-[30px] shrink-0">
              <StarIcon />
            </div>
            <div className="flex-1 space-y-1">
              <h3 className="font-bold">
                {m.subscription_walkthrough_p3_trail_end()}
              </h3>
              <p className="text-sm">
                {m.subscription_walkthrough_p3_trail_end_note()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed bottom CTA */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-[#001721] px-6 pb-12 pt-3"
        style={{
          boxShadow: '0 -3px 3px 0 rgba(255, 255, 255, 0.20)',
        }}
      >
        <div className="mx-auto max-w-[640px] space-y-3">
          <button
            className="group h-[52px] w-full rounded-xl p-px [background-image:linear-gradient(180deg,#C5FF70_0%,#69FF78_45%,#30D3FF_100%)] disabled:opacity-50"
            disabled={props.isPending}
            onClick={props.onNext}
          >
            <span className="flex h-full w-full items-center justify-center rounded-[11px] font-bold text-[#176361] [background-image:linear-gradient(106deg,#C5FF70_13%,#30D3FF_97%)] group-active:[background-image:linear-gradient(90deg,#94D66E_0%,#2BB8D3_100%)]">
              {m.choose_plan()}
            </span>
          </button>

          <p className="text-center text-white/90">
            {m.subscription_footer_yearly_free_trial({
              free_trial_day: freeTrialDays,
              yearly_price: removeTrailingZeros(product?.price_localized ?? ''),
            })}
          </p>
        </div>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Plan select view
// ---------------------------------------------------------------------------

function PlanSelectView(props: {
  isTrial: boolean
  isPending: boolean
  onBack: () => void
  onSubscribe: (product: Product) => void
}) {
  const [selectedPlan, setSelectedPlan] = useState<'annual' | 'monthly'>(
    'annual',
  )
  const products = useStore($products)
  const isChinaAndroid = platform === 'china_android'

  const getSubscriptionInfoURL = () => {
    switch (import.meta.env.MODE) {
      case 'staging':
        return `https://forest-permanent-paywall-staging.seekrtech.com/${language}/details/`
      case 'production':
        return `https://forest-permanent-paywall.seekrtech.com/${language}/details/`
      default:
        return `https://forest-permanent-paywall.seekrtech.com/${language}/details/`
    }
  }

  if (!products) return null

  const monthlyProduct =
    platform !== 'china_android' && props.isTrial && products.monthlyNoTrial
      ? products.monthlyNoTrial
      : products.monthlyEarlyBird

  const selectedProduct =
    selectedPlan === 'annual' ? products.annualEarlyBird : monthlyProduct

  const isSelectedTrial =
    props.isTrial && selectedProduct.eligible_trial_days > 0

  const getTitleText = () => {
    if (selectedPlan === 'annual') {
      return m.subscription_walkthrough_p4_title({
        early_bird_discount_percent: Math.round(
          ((products.annualOriginal.price_value -
            products.annualEarlyBird.price_value) /
            products.annualOriginal.price_value) *
            100,
        ),
      })
    }
    return m.subscription_walkthrough_p4_title({
      early_bird_discount_percent: Math.round(
        ((products.monthlyOriginal.price_value - monthlyProduct.price_value) /
          products.monthlyOriginal.price_value) *
          100,
      ),
    })
  }

  const getButtonText = () => {
    if (isSelectedTrial) {
      return m.btn_start_free_trial_day({
        free_trial_day: selectedProduct.eligible_trial_days,
      })
    }
    return m.btn_unlock_plus()
  }

  return (
    <>
      {/* Nav bar */}
      <div className="absolute left-0 right-0 top-0 mx-auto flex w-full max-w-[640px] justify-between px-4 pt-[calc(16px+var(--safe-area-top))]">
        <button
          className="size-[42px] rounded-[50%] bg-white/80 backdrop-blur"
          onClick={props.onBack}
          disabled={props.isPending}
        >
          <BackButtonIcon />
        </button>
        <div></div>
      </div>

      {/* Scrollable content */}
      <div className="h-full space-y-12 overflow-y-auto px-8 pb-72 pt-[120px]">
        <h2
          className="whitespace-pre-line px-4 text-center text-2xl font-bold text-white"
          dangerouslySetInnerHTML={{
            __html: getTitleText().replace('[%2$s]', 'color: #2AFD00;'),
          }}
        />

        <div className="mx-auto max-w-[640px] space-y-3">
          <PlanOption
            plan="annual"
            title={isChinaAndroid ? m.yearly_plan_cn() : m.yearly_plan()}
            earlyBirdProduct={products.annualEarlyBird}
            originalProduct={products.annualOriginal}
            selected={selectedPlan === 'annual'}
            selectPlan={setSelectedPlan}
          />
          <PlanOption
            plan="monthly"
            title={isChinaAndroid ? m.monthly_plan_cn() : m.monthly_plan()}
            earlyBirdProduct={monthlyProduct}
            originalProduct={products.monthlyOriginal}
            selected={selectedPlan === 'monthly'}
            selectPlan={setSelectedPlan}
          />
        </div>
      </div>

      {/* Fixed bottom CTA */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-[#001721] px-6 pb-12 pt-3"
        style={{
          boxShadow: '0 -3px 3px 0 rgba(255, 255, 255, 0.20)',
        }}
      >
        <div className="mx-auto max-w-[640px] space-y-3">
          {isSelectedTrial && (
            <p className="text-center text-[#FFFFFFE6]">
              {m.subscription_walkthrough_p1_trial_fee()}
            </p>
          )}
          <button
            className="group h-[52px] w-full rounded-xl p-px [background-image:linear-gradient(180deg,#C5FF70_0%,#69FF78_45%,#30D3FF_100%)] disabled:opacity-50"
            disabled={props.isPending}
            onClick={() => props.onSubscribe(selectedProduct)}
          >
            <span className="flex h-full w-full items-center justify-center rounded-[11px] font-bold text-[#176361] [background-image:linear-gradient(106deg,#C5FF70_13%,#30D3FF_97%)] group-active:[background-image:linear-gradient(90deg,#94D66E_0%,#2BB8D3_100%)]">
              {getButtonText()}
            </span>
          </button>

          <a
            className="grid h-10 w-full place-items-center font-bold text-[#FFFFFF99]"
            href={`${getSubscriptionInfoURL()}?platform=${platform}`}
          >
            {m.subscription_view_complete_benefit()}
          </a>

          {platform !== 'china_android' && (
            <p className="text-center text-xs text-[#FFFFFF99]">
              {m.subscription_walkthrough_p4_footer_notes()}
            </p>
          )}
        </div>
      </div>
    </>
  )
}
